/**
 * Réglages dont une installation Ghost auto-hébergée a besoin, et que le fichier ne contient pas
 * forcément.
 *
 * L'éditeur ne pouvait proposer que les clés **déjà présentes** dans `config.production.json`. Or
 * une installation neuve n'en contient presque aucune : ni infolettres, ni statistiques, ni
 * réglages de sécurité. Il fallait donc les connaître à l'avance pour pouvoir les mettre en place,
 * plutôt que d'aller les écrire à la main en SSH — ce que Vigie existe précisément pour éviter.
 *
 * Les noms de clés ne sont pas déduits de la documentation : ils ont été relevés dans le code de
 * Ghost 6.57.1 tel qu'installé en production (`config.get('tinybird:tracker')`,
 * `mailgunConfig.apiKey`, `security:staffDeviceVerification`…). C'est la seule source qui ne se
 * trompe pas de version, et une clé mal nommée est ignorée en silence par Ghost — le pire des
 * échecs, puisqu'on croit avoir configuré quelque chose.
 */
/** Nature d'un réglage, qui détermine le champ affiché et la conversion de la saisie. */
export const TYPES_RÉGLAGE = ['texte', 'url', 'nombre', 'booleen', 'secret', 'choix'];
/**
 * Bases de l'API Mailgun.
 *
 * Le compte est rattaché à une région, et viser la mauvaise base donne un « domain not found »
 * parfaitement trompeur : le domaine existe, mais pas dans cette région.
 */
const BASES_MAILGUN = ['https://api.eu.mailgun.net/v3', 'https://api.mailgun.net/v3'];
export const RÉGLAGES_GHOST = [
    {
        clé: 'mail',
        titre: 'Courriels transactionnels',
        résumé: 'Invitations de l’équipe, réinitialisations de mot de passe, liens de connexion des membres. ' +
            'Sans transport configuré, Ghost tente un envoi direct qui finit presque toujours en indésirables — ' +
            'et personne ne peut plus se connecter à l’administration.',
        réglages: [
            {
                chemin: 'mail.transport',
                libellé: 'Transport',
                aide: 'SMTP dans presque tous les cas. « Direct » tente une remise sans relais et échoue en pratique.',
                type: 'choix',
                choix: ['SMTP', 'Sendmail', 'Direct'],
                requis: true,
            },
            {
                chemin: 'mail.from',
                libellé: 'Expéditeur',
                aide: 'Adresse d’expédition. Elle doit appartenir à un domaine que le relais accepte de signer.',
                type: 'texte',
                exemple: '"Nom du média" <bonjour@exemple.fr>',
            },
            {
                chemin: 'mail.options.host',
                libellé: 'Hôte SMTP',
                aide: 'Serveur du relais. Requis avec le transport SMTP, sauf si un service nommé est indiqué.',
                type: 'texte',
                exemple: 'mail.infomaniak.com',
                requis: true,
            },
            {
                chemin: 'mail.options.port',
                libellé: 'Port SMTP',
                aide: '587 avec STARTTLS, 465 en TLS implicite. Le port 25 est bloqué chez la plupart des hébergeurs.',
                type: 'nombre',
                exemple: '587',
            },
            {
                chemin: 'mail.options.secure',
                libellé: 'TLS implicite',
                aide: 'Vrai pour le port 465, faux pour 587 — où le chiffrement est négocié après connexion.',
                type: 'booleen',
            },
            {
                chemin: 'mail.options.auth.user',
                libellé: 'Compte SMTP',
                aide: 'Identifiant fourni par le relais.',
                type: 'texte',
            },
            {
                chemin: 'mail.options.auth.pass',
                libellé: 'Mot de passe SMTP',
                aide: 'Conservé chiffré par Vigie, masqué à l’affichage, révélable champ par champ avec trace dans l’audit.',
                type: 'secret',
            },
        ],
    },
    {
        clé: 'bulkEmail',
        titre: 'Infolettres',
        résumé: 'L’envoi en masse ne passe pas par le SMTP transactionnel : Ghost n’a d’implémentation que ' +
            'pour Mailgun, en dur. Sans ces clés, l’onglet des infolettres reste inactif et publier une ' +
            'lettre n’envoie rien — sans message d’erreur explicite.',
        condition: 'Ghost lit apiKey, domain et baseUrl ; les trois sont nécessaires. Il appelle ensuite ' +
            'baseUrl/{domain}/messages, puis interroge baseUrl/{domain}/events pour les statistiques.',
        modes: [
            {
                clé: 'proxymail',
                titre: 'Socle ProxyMail',
                résumé: 'Ghost croit parler à Mailgun ; le proxy achemine vers le fournisseur réel choisi pour ce ' +
                    'client — Brevo, Scaleway TEM, SES, SMTP — et réexpose les statistiques au format ' +
                    'Mailgun. Le fournisseur se règle dans ProxyMail, pas ici : changer de fournisseur ne ' +
                    'touche donc plus à la configuration du site.',
            },
            {
                clé: 'mailgun',
                titre: 'Mailgun direct',
                résumé: 'Le service Mailgun lui-même. Plus court, mais chaque site porte alors les identifiants ' +
                    'du compte d’envoi, et changer de fournisseur demande de rouvrir chaque configuration.',
            },
        ],
        réglages: [
            {
                chemin: 'bulkEmail.mailgun.apiKey',
                libellé: 'Clé d’API',
                aide: 'Clé privée du compte d’envoi. Conservée chiffrée par Vigie.',
                type: 'secret',
                requis: true,
                variantes: {
                    proxymail: {
                        aide: 'Clé du client dans ProxyMail, de la forme « key- » suivie de 32 caractères ' +
                            'hexadécimaux. Elle ne donne accès qu’à ce client, et se révoque côté proxy sans ' +
                            'toucher au site.',
                    },
                    mailgun: { aide: 'Clé privée du compte, depuis la console Mailgun.' },
                },
            },
            {
                chemin: 'bulkEmail.mailgun.domain',
                libellé: 'Domaine d’envoi',
                aide: 'Domaine d’expédition des infolettres, distinct du domaine du site en général.',
                type: 'texte',
                exemple: 'lettres.exemple.fr',
                requis: true,
                variantes: {
                    proxymail: { aide: 'Domaine d’envoi déclaré pour ce client dans ProxyMail.' },
                    mailgun: {
                        aide: 'Domaine vérifié chez Mailgun, souvent un sous-domaine dédié pour préserver la ' +
                            'réputation du domaine principal.',
                    },
                },
            },
            {
                chemin: 'bulkEmail.mailgun.baseUrl',
                libellé: 'Base de l’API',
                aide: 'Ghost y ajoute /{domaine}/messages : la base doit donc se terminer par /v3.',
                type: 'url',
                requis: true,
                variantes: {
                    proxymail: {
                        aide: 'Adresse publique de ProxyMail suivie de /v3. Ghost y ajoute /{domaine}/messages, ' +
                            'puis interroge /{domaine}/events pour les statistiques : sans le /v3, tout répond 404 ' +
                            'et l’envoi échoue sans raison lisible.',
                        exemple: 'https://proxymail.exemple.org/v3',
                    },
                    mailgun: {
                        type: 'choix',
                        choix: BASES_MAILGUN,
                        aide: 'Région du compte Mailgun. Se tromper donne un « domain not found » alors que le ' +
                            'domaine existe — dans l’autre région.',
                    },
                },
            },
            {
                chemin: 'bulkEmail.mailgun.tag',
                libellé: 'Étiquette',
                aide: 'Étiquette posée sur chaque envoi, pour retrouver le trafic d’un site dans un compte d’envoi partagé.',
                type: 'texte',
            },
            {
                chemin: 'bulkEmail.batchSize',
                libellé: 'Taille des lots',
                aide: '1000 par défaut. À baisser si le relais applique une limite plus stricte par requête.',
                type: 'nombre',
                exemple: '1000',
            },
        ],
    },
    {
        clé: 'tinybird',
        titre: 'Statistiques de trafic',
        résumé: 'Les statistiques de Ghost 6 s’appuient sur un collecteur d’événements. Deux voies : le ' +
            'service Tinybird, ou un collecteur à soi — Socle Analytics — auquel Ghost parle par la même ' +
            'interface. Sans configuration, l’onglet Statistiques reste vide, sans expliquer pourquoi.',
        condition: 'Ghost exige tinybird.tracker.endpoint, plus un moyen de lire : workspaceId et adminToken ' +
            '(il signe alors un jeton), ou stats.local.enabled avec son propre point de lecture.',
        modes: [
            {
                clé: 'edge',
                titre: 'Socle Analytics (collecteur à soi)',
                résumé: 'Les événements partent vers votre propre collecteur, et la lecture se fait par son ' +
                    'point d’accès. Aucune donnée de lecture ne quitte votre infrastructure, et il n’y a ni ' +
                    'espace de travail ni jeton d’administration à gérer.',
                // C'est ce drapeau qui fait lire Ghost du côté « local » plutôt que du côté Tinybird.
                valeurs: { 'tinybird.stats.local.enabled': 'true' },
            },
            {
                clé: 'api',
                titre: 'Tinybird (API)',
                résumé: 'Le service hébergé. Ghost signe lui-même un jeton de lecture à partir de l’espace de ' +
                    'travail et du jeton d’administration, restreint aux statistiques de ce site.',
                valeurs: { 'tinybird.stats.local.enabled': 'false' },
            },
        ],
        réglages: [
            {
                chemin: 'tinybird.tracker.endpoint',
                libellé: 'Point de collecte',
                aide: 'Où le script de suivi dépose les événements. Sans lui, Ghost n’active aucune statistique.',
                type: 'url',
                requis: true,
            },
            {
                chemin: 'tinybird.tracker.token',
                libellé: 'Jeton de collecte',
                aide: 'Jeton d’écriture, exposé au navigateur : il ne doit donner que le droit d’ajouter des événements.',
                type: 'secret',
            },
            {
                chemin: 'tinybird.tracker.datasource',
                libellé: 'Source de données',
                aide: 'Nom de la table d’événements visée par la collecte.',
                type: 'texte',
                exemple: 'analytics_events',
            },
            {
                chemin: 'tinybird.stats.local.endpoint',
                libellé: 'Point de lecture',
                aide: 'Où l’administration lit les statistiques agrégées.',
                type: 'url',
                modes: ['edge'],
                requis: true,
            },
            {
                chemin: 'tinybird.stats.local.token',
                libellé: 'Jeton de lecture',
                aide: 'Jeton de lecture du collecteur. Il reste côté serveur, contrairement au jeton de collecte.',
                type: 'secret',
                modes: ['edge'],
            },
            {
                chemin: 'tinybird.workspaceId',
                libellé: 'Espace de travail',
                aide: 'Identifiant de l’espace Tinybird. Ghost l’inscrit dans le jeton qu’il signe pour lire.',
                type: 'texte',
                modes: ['api'],
                requis: true,
            },
            {
                chemin: 'tinybird.adminToken',
                libellé: 'Jeton d’administration',
                aide: 'Sert uniquement à signer des jetons de lecture restreints à ce site. Il ne quitte jamais le serveur.',
                type: 'secret',
                modes: ['api'],
                requis: true,
            },
            {
                chemin: 'tinybird.stats.endpoint',
                libellé: 'Point de lecture Tinybird',
                aide: 'Base de l’API de votre région Tinybird, telle qu’indiquée dans la console de l’espace de travail.',
                type: 'url',
                modes: ['api'],
            },
            {
                chemin: 'tinybird.stats.id',
                libellé: 'Identifiant de site',
                aide: 'Remplace l’identifiant interne du site dans les requêtes. À laisser vide sauf besoin précis.',
                type: 'texte',
            },
        ],
    },
    {
        clé: 'security',
        titre: 'Sécurité des comptes',
        résumé: 'Deux réglages qui ne se remarquent qu’au mauvais moment : celui qui peut enfermer dehors ' +
            'toute l’équipe, et celui qui laisse un webhook viser le réseau interne.',
        réglages: [
            {
                chemin: 'security.staffDeviceVerification',
                libellé: 'Vérification de l’appareil',
                aide: 'Actif par défaut : Ghost envoie un code par courriel à chaque connexion depuis un nouvel ' +
                    'appareil. Sur une installation dont le courriel transactionnel ne fonctionne pas encore, ' +
                    'plus personne ne peut se connecter. À désactiver le temps de régler le courriel — puis à ' +
                    'réactiver.',
                type: 'booleen',
            },
            {
                chemin: 'security.allowWebhookInternalIPs',
                libellé: 'Webhooks vers le réseau interne',
                aide: 'À laisser faux. Vrai permet à un webhook de viser une adresse privée du serveur, ce qui ' +
                    'transforme un compte auteur en moyen d’explorer le réseau interne.',
                type: 'booleen',
            },
        ],
    },
    {
        clé: 'exploitation',
        titre: 'Images, journaux et vie privée',
        résumé: 'Réglages d’exploitation d’un serveur qu’on gère soi-même : ce qui remplit le disque, et ce ' +
            'qui sort vers des tiers.',
        réglages: [
            {
                chemin: 'imageOptimization.resize',
                libellé: 'Redimensionnement des images',
                aide: 'Ghost engendre les tailles intermédiaires. Coûteux à l’envoi, économe à chaque visite ensuite.',
                type: 'booleen',
            },
            {
                chemin: 'imageOptimization.srcsets',
                libellé: 'Jeux de tailles responsives',
                aide: 'Produit les variantes servies selon la largeur d’écran. À désactiver seulement sur un serveur très contraint.',
                type: 'booleen',
            },
            {
                chemin: 'logging.level',
                libellé: 'Niveau de journalisation',
                aide: '« info » convient. « debug » remplit le disque en quelques jours sur un site actif.',
                type: 'choix',
                choix: ['error', 'warn', 'info', 'debug'],
            },
            {
                chemin: 'logging.rotation.enabled',
                libellé: 'Rotation des journaux',
                aide: 'Désactivée par défaut, ce qui laisse un fichier grossir sans fin. À activer sur un hébergement à quota.',
                type: 'booleen',
            },
            {
                chemin: 'logging.rotation.count',
                libellé: 'Journaux conservés',
                aide: 'Nombre de fichiers gardés après rotation.',
                type: 'nombre',
                exemple: '10',
            },
            {
                chemin: 'privacy.useGravatar',
                libellé: 'Avatars Gravatar',
                aide: 'À faux, Ghost n’interroge plus Gravatar : l’adresse de chaque autrice et auteur cesse ' +
                    'd’être transmise, sous forme d’empreinte, à un service tiers.',
                type: 'booleen',
            },
        ],
    },
];
/** Tous les chemins du catalogue, sans doublon. */
export const CHEMINS_RÉGLAGES_GHOST = RÉGLAGES_GHOST.flatMap((groupe) => groupe.réglages.map((réglage) => réglage.chemin));
export function réglageGhost(chemin) {
    for (const groupe of RÉGLAGES_GHOST) {
        const trouvé = groupe.réglages.find((réglage) => réglage.chemin === chemin);
        if (trouvé !== undefined)
            return trouvé;
    }
    return undefined;
}
/**
 * Réglages d'un groupe qui s'appliquent au mode retenu, variantes comprises.
 *
 * Le filtrage écarte les clés propres aux autres modes ; la variante adapte la présentation des
 * clés communes. Les deux sont nécessaires : sans le premier on demande un jeton d'administration
 * Tinybird à qui héberge son propre collecteur, sans la seconde on impose une liste de deux régions
 * Mailgun à qui vise son propre proxy.
 */
export function réglagesDuMode(groupe, mode) {
    return groupe.réglages
        .filter((réglage) => réglage.modes === undefined || (mode !== null && réglage.modes.includes(mode)))
        .map((réglage) => {
        const variante = mode === null ? undefined : réglage.variantes?.[mode];
        return variante === undefined ? réglage : { ...réglage, ...variante };
    });
}
/**
 * Mode déjà en place, déduit de la configuration.
 *
 * Faire rechoisir à chaque ouverture ce qui est déjà configuré donnerait un site parfaitement
 * réglé qui a l'air en attente d'une décision. La déduction vit ici, avec le catalogue, plutôt que
 * dans le composant : c'est de la connaissance sur Ghost, et elle se teste.
 */
export function modeEnPlace(groupe, valeurs) {
    if (groupe.modes === undefined)
        return null;
    if (groupe.clé === 'tinybird') {
        const local = valeurs['tinybird.stats.local.enabled'];
        if (local === true || local === 'true')
            return 'edge';
        return valeurs['tinybird.workspaceId'] === undefined ? null : 'api';
    }
    if (groupe.clé === 'bulkEmail') {
        const base = valeurs['bulkEmail.mailgun.baseUrl'];
        if (typeof base !== 'string' || base === '')
            return null;
        // L'hôte tranche : tout ce qui ne va pas chez Mailgun passe par un proxy compatible.
        return /(^|\.)mailgun\.net(\/|$)/.test(base) ? 'mailgun' : 'proxymail';
    }
    return null;
}
/** Lit une valeur par chemin aplati, sans jeter sur un chemin absent. */
function lire(config, chemin) {
    let courant = config;
    for (const segment of chemin.split('.')) {
        if (courant === null || typeof courant !== 'object')
            return undefined;
        courant = courant[segment];
    }
    return courant;
}
function renseigné(valeur) {
    return valeur !== undefined && valeur !== null && valeur !== '';
}
/**
 * Ce qui ne fonctionnera pas, sans empêcher d'enregistrer.
 *
 * La validation refuse ce qui casse Ghost au démarrage. Ces avertissements couvrent l'autre
 * danger, plus sournois : une configuration parfaitement valide dont une fonctionnalité reste
 * **silencieusement inactive**. Ghost n'en dit rien — il se contente d'un message de débogage que
 * personne ne lit — et l'on découvre trois semaines plus tard que les infolettres ne sont jamais
 * parties.
 *
 * Ils ne bloquent pas : on enregistre souvent une configuration à moitié faite, en attendant une
 * clé d'API. Refuser cet état intermédiaire obligerait à tout saisir d'un coup.
 *
 * Chaque règle reprend une condition lue dans le code de Ghost 6.57.1, pas une supposition.
 */
export function avertissementsGhost(config) {
    const avertissements = [];
    // --- Statistiques ------------------------------------------------------------------------------
    const tinybird = lire(config, 'tinybird');
    if (tinybird !== undefined && tinybird !== null) {
        const pointDeCollecte = renseigné(lire(config, 'tinybird.tracker.endpoint'));
        const parJeton = renseigné(lire(config, 'tinybird.workspaceId')) &&
            renseigné(lire(config, 'tinybird.adminToken'));
        const parCollecteurLocal = lire(config, 'tinybird.stats.local.enabled') === true;
        if (!pointDeCollecte) {
            avertissements.push({
                groupe: 'tinybird',
                message: 'Statistiques inactives : Ghost exige tinybird.tracker.endpoint avant toute autre clé.',
            });
        }
        else if (!parJeton && !parCollecteurLocal) {
            avertissements.push({
                groupe: 'tinybird',
                message: 'Statistiques inactives : la collecte est configurée mais rien ne permet de les lire. ' +
                    'Il faut soit workspaceId et adminToken, soit tinybird.stats.local.enabled avec son ' +
                    'point de lecture.',
            });
        }
        else if (parCollecteurLocal && !renseigné(lire(config, 'tinybird.stats.local.endpoint'))) {
            avertissements.push({
                groupe: 'tinybird',
                message: 'Lecture des statistiques incomplète : tinybird.stats.local.enabled est actif sans ' +
                    'tinybird.stats.local.endpoint.',
            });
        }
    }
    // --- Infolettres -------------------------------------------------------------------------------
    const mailgun = ['apiKey', 'domain', 'baseUrl'].map((clé) => renseigné(lire(config, `bulkEmail.mailgun.${clé}`)));
    if (mailgun.some(Boolean) && !mailgun.every(Boolean)) {
        avertissements.push({
            groupe: 'bulkEmail',
            message: 'Infolettres inactives : Ghost a besoin des trois clés — apiKey, domain et baseUrl. ' +
                'Publier une lettre n’enverra rien tant qu’il en manque une.',
        });
    }
    const base = lire(config, 'bulkEmail.mailgun.baseUrl');
    if (typeof base === 'string' && base !== '') {
        /*
         * Ghost concatène : baseUrl + /{domaine}/messages, puis /{domaine}/events pour les statistiques.
         *
         * Une base sans `/v3` donne donc des 404 sur les deux, et l'envoi échoue sans raison lisible —
         * le piège le plus facile à tendre en pointant vers un proxy compatible, où l'adresse publique
         * seule paraît suffire.
         */
        if (!/\/v3\/?$/.test(base)) {
            avertissements.push({
                groupe: 'bulkEmail',
                message: 'La base de l’API ne se termine pas par /v3 : Ghost y ajoute /{domaine}/messages, et ' +
                    'l’appel répondra 404. C’est vrai de Mailgun comme d’un proxy compatible.',
            });
        }
        const clé = lire(config, 'bulkEmail.mailgun.apiKey');
        const versMailgun = /(^|\.)mailgun\.net(\/|$)/.test(base);
        if (!versMailgun &&
            typeof clé === 'string' &&
            clé !== '' &&
            !/^key-[0-9a-f]{32}$/.test(clé.trim())) {
            avertissements.push({
                groupe: 'bulkEmail',
                message: 'La base ne pointe pas vers Mailgun : s’il s’agit de Socle ProxyMail, la clé attendue ' +
                    'est de la forme « key- » suivie de 32 caractères hexadécimaux. Une autre forme sera ' +
                    'refusée à l’authentification.',
            });
        }
    }
    // --- Enfermement dehors ------------------------------------------------------------------------
    const transport = lire(config, 'mail.transport');
    const relaisConfiguré = transport === 'SMTP'
        ? renseigné(lire(config, 'mail.options.host')) ||
            renseigné(lire(config, 'mail.options.service'))
        : transport === 'Sendmail';
    if (!relaisConfiguré && lire(config, 'security.staffDeviceVerification') !== false) {
        avertissements.push({
            groupe: 'security',
            message: 'Risque de blocage : la vérification de l’appareil est active alors que l’envoi de ' +
                'courriels n’est pas configuré. Ghost demandera un code qu’il ne saura pas envoyer, et ' +
                'plus personne ne pourra se connecter à l’administration.',
        });
    }
    // --- URL publique ------------------------------------------------------------------------------
    const url = lire(config, 'url');
    if (typeof url === 'string' && url.startsWith('http://')) {
        avertissements.push({
            groupe: 'url',
            message: 'URL en http:// : les cookies de session et l’espace membre se comportent mal hors TLS. ' +
                'Vérifie que ce n’est pas un oubli.',
        });
    }
    return avertissements;
}
//# sourceMappingURL=ghost-settings.js.map